import { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, Minimize2, Maximize2 } from 'lucide-react';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'ai', text: "Hi! I'm your AI assistant. How can I help you today?" }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const chatbotRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      const userMessage = { type: 'user', text: inputValue };
      setMessages([...messages, userMessage]);
      const message = inputValue;
      setInputValue('');

      // Simulate AI response
      setTimeout(() => {
        const responses = [
          "That's a great question! Let me help you with that.",
          "I understand. Here's what I can tell you...",
          "Interesting! Let me think about that for a moment.",
          "I'm here to assist you. What would you like to know?",
          "Thanks for asking! Here's some information that might help.",
          "I'm processing your request. One moment please...",
          "Great to hear from you! How can I assist you further?",
          "I'm happy to help! Let me provide some guidance.",
        ];
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        const aiMessage = { type: 'ai', text: randomResponse };
        setMessages(prev => [...prev, aiMessage]);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.chat-header') && !e.target.closest('button')) {
      setIsDragging(true);
      const rect = chatbotRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep chatbot within viewport
      const maxX = window.innerWidth - 400;
      const maxY = window.innerHeight - 100;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  return (
    <>
      <div
        ref={chatbotRef}
        className={`chatbot ${isOpen ? 'open' : ''} ${isMinimized ? 'minimized' : ''}`}
        style={{ left: position.x, top: position.y }}
        onMouseDown={handleMouseDown}
      >
        <div className="chat-header">
          <div className="chat-header-content">
            <div className="chat-avatar">
              <Bot size={20} />
            </div>
            <span className="chat-title">AI Assistant</span>
          </div>
          <div className="chat-header-actions">
            <button
              className="chat-header-button"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? 'Expand' : 'Minimize'}
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            </button>
            <button
              className="chat-header-button"
              onClick={() => setIsOpen(false)}
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.type === 'ai' ? 'ai-message' : 'user-message'}`}>
                  {msg.type === 'ai' && (
                    <div className="message-avatar">
                      <Bot size={16} />
                    </div>
                  )}
                  <div className="message-content">
                    <p>{msg.text}</p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-container">
              <textarea
                className="chat-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                rows={1}
              />
              <button className="send-button" onClick={handleSendMessage}>
                <Send size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="Open AI Assistant"
      >
        <Bot size={24} />
      </button>
    </>
  );
};

export default Chatbot;
